import { getAuth } from "firebase/auth";
import { getAuthCentral } from "../../config/FirebaseConfig.js";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  updateDoc,
  arrayUnion,
  getDocs,
  deleteDoc,
  limit,
  orderBy,
} from "firebase/firestore";
import { db } from "../../config/FirebaseConfig.js";
import addLogDocument from "../addLogDocument.js";
import { checkDB } from "../../models/DataFunctions.js";

/**
 * Extract folder information from a file using OCR + AI
 * @param {File|Blob} file - The file to extract information from
 * @param {string} token - Authentication token
 * @param {string} prefix - API URL prefix
 * @param {object} getEventsByOrgRef - Reference for logging events
 * @returns {Promise<object>} Extracted folder information
 */
export default async function extractInfosFromFile(
  file,
  token,
  prefix,
  getEventsByOrgRef = null,
) {
  const auth = getAuthCentral();
  const currentUser = auth.currentUser;

  if (!currentUser) {
    console.error("User not logged in");
    throw new Error("User not logged in");
  }

  const userRef = doc(db, "user", currentUser.uid);

  try {
    const start = new Date();

    // Prepare the file for upload
    const formData = new FormData();

    // Handle both File objects and Blob objects
    if (file instanceof File) {
      formData.append("file", file);
    } else if (file instanceof Blob) {
      const fileName = "document.pdf";
      formData.append("file", file, fileName);
    } else if (typeof file === "string") {
      // If file is a URL, download it first
      const fileResponse = await fetch(file);
      if (!fileResponse.ok) {
        throw new Error(`Failed to download file: ${fileResponse.status}`);
      }
      const fileBlob = await fileResponse.blob();
      const fileName =
        file.split("/").pop().replace(/\s+/g, "_") || "document.pdf";
      formData.append("file", fileBlob, fileName);
    } else {
      throw new Error("Invalid file format");
    }

    // API endpoint for extraction
    const url = `${prefix}extract/file`;

    // Send the request
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const end = new Date();
    const elapsed = end - start;

    // Log the API call
    if (getEventsByOrgRef) {
      await addLogDocument(
        url,
        prefix,
        userRef,
        getEventsByOrgRef,
        response.status.toString(),
        "",
        "",
        JSON.stringify({ action: "extract_folder_info" }),
        response.statusText,
        "POST",
        elapsed,
      );
    }

    if (response.ok) {
      const extractedData = await response.json();
      console.log("Extracted folder information:", extractedData);
      return extractedData;
    } else {
      const errorText = await response.text();
      console.error(`Extraction failed: ${response.status} - ${errorText}`);
      throw new Error(`Extraction failed: ${response.status}`);
    }
  } catch (error) {
    console.error(`Error extracting folder information: ${error.message}`);
    throw error;
  }
}
